import { createRouter, createWebHashHistory } from 'vue-router'
import { canAccessPage, pageByPath } from '@/data/registry'
import { adapter } from '@/services/prototype-adapter'
import { pinia } from '@/stores'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useWorkspaceStore } from '@/stores/workspace'
import { buildRoutes } from './routes'

export const routes = buildRoutes()

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.path === '/login') return { top: 0 }
    return { top: 0 }
  },
})

function safeRedirect(value: unknown): string {
  const text = typeof value === 'string' ? value : ''
  if (!text.startsWith('/') || text.startsWith('//')) return '/dashboard'
  return text
}

/**
 * While maintenance mode is on, business pages are replaced by the shared
 * maintenance surface. Session, profile and the two configuration pages stay
 * reachable so an authorized operator can turn maintenance off again.
 */
const MAINTENANCE_ALLOWED = new Set([
  '/maintenance',
  '/login',
  '/403',
  '/404',
  '/500',
  '/offline',
  '/profile',
  '/settings/general',
  '/settings/operations',
])

router.beforeEach(to => {
  const auth = useAuthStore(pinia)
  const ui = useUiStore(pinia)
  ui.beginRoute()
  document.title = `${String(to.meta.title ?? '运营工作台')} · 康护健康`

  if (to.path === '/login') {
    if (auth.isAuthenticated) return safeRedirect(to.query.redirect)
    return true
  }

  const page = pageByPath(to.path)
  const requiresAuth = page ? page.permission !== 'public' : Boolean(to.meta.requiresAuth)
  if (!auth.isAuthenticated && requiresAuth) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  if (page && auth.isAuthenticated && !canAccessPage(page, auth.has)) {
    return { path: '/403', query: { from: to.fullPath, need: page.permission } }
  }
  if (to.path === '/403' && !auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  if (auth.isAuthenticated && adapter.settings.maintenanceEnabled && !MAINTENANCE_ALLOWED.has(to.path)) {
    return { path: '/maintenance', query: { redirect: to.fullPath } }
  }
  return true
})

router.afterEach(to => {
  const auth = useAuthStore(pinia)
  const ui = useUiStore(pinia)
  const workspace = useWorkspaceStore(pinia)
  if (to.path !== '/login') workspace.visit(to)
  if (auth.isAuthenticated) workspace.activate(to.path)
  ui.endRoute()
})

router.onError(() => {
  const ui = useUiStore(pinia)
  ui.endRoute()
})

export default router
